import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Clock,
  DollarSign,
  Wrench,
  FileText,
  Copy,
  RefreshCw,
  Settings,
  LogOut,
  History,
  Save,
  Sparkles,
  FileSpreadsheet,
  Flame,
  Droplets,
  SlidersHorizontal,
  Plus,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Generate labor hours options in 10 minute intervals (0.167 hours)
const laborHoursOptions = [];
for (let mins = 10; mins <= 720; mins += 10) { // 10 mins to 12 hours
  const hours = mins / 60;
  laborHoursOptions.push({
    value: hours,
    label: formatHoursDisplay(hours)
  });
}

// Format hours to display (e.g., 1.33 -> "1 hour 20 mins")
function formatHoursDisplay(hours) {
  const totalMinutes = Math.round(hours * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (wholeHours === 0) {
    return `${minutes} mins`;
  } else if (minutes === 0) {
    return wholeHours === 1 ? `1 hour` : `${wholeHours} hours`;
  } else {
    const hourText = wholeHours === 1 ? `1 hour` : `${wholeHours} hours`;
    return `${hourText} ${minutes} mins`;
  }
}

// Calculate working window (30 mins before, 1 hour after)
function getWorkingWindow(hours) {
  const minHours = Math.max(0, hours - 0.5); // 30 mins before, min 0
  const maxHours = hours + 1; // 1 hour after
  return {
    min: formatHoursDisplay(minHours),
    max: formatHoursDisplay(maxHours)
  };
}

export default function TechnicianDashboard() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();

  const [settings, setSettings] = useState(null);
  const [tradeType, setTradeType] = useState("plumbing"); // "plumbing" or "hvac"
  const [laborHours, setLaborHours] = useState(1); // in hours (decimal)
  const [materialsCost, setMaterialsCost] = useState("");
  const [permitFee, setPermitFee] = useState("");
  const [subcontractorCost, setSubcontractorCost] = useState("");
  const [managerDiscount, setManagerDiscount] = useState("");
  const [profitMargin, setProfitMargin] = useState(0.57); // Default middle of range (52-62%)
  const [technicianNotes, setTechnicianNotes] = useState("");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [customerPrice, setCustomerPrice] = useState(null);
  const [formattedPrice, setFormattedPrice] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedOption, setLastSavedOption] = useState(null);

  // Auto-save when an option is selected
  useEffect(() => {
    const autoSave = async () => {
      if (!selectedOption || !customerPrice || !technicianNotes.trim()) return;
      if (lastSavedOption?.tier === selectedOption.tier && lastSavedOption?.price === selectedOption.price) return;
      
      setIsSaving(true);
      try {
        await axios.post(
          `${API_URL}/job-entries`,
          {
            labor_hours: laborHours,
            materials_cost: parseFloat(materialsCost) || 0,
            permit_fee: parseFloat(permitFee) || 0,
            subcontractor_cost: parseFloat(subcontractorCost) || 0,
            technician_notes: technicianNotes,
            customer_summary: `${selectedOption.name}: ${selectedOption.description}`,
            customer_price: selectedOption.price || 0,
          },
          { headers: getAuthHeader() }
        );
        setLastSavedOption(selectedOption);
        toast.success("Entry auto-saved!");
      } catch (error) {
        console.error("Auto-save error:", error);
      } finally {
        setIsSaving(false);
      }
    };

    const debounce = setTimeout(autoSave, 1000);
    return () => clearTimeout(debounce);
  }, [selectedOption, customerPrice, technicianNotes, laborHours, materialsCost, permitFee, subcontractorCost, getAuthHeader, lastSavedOption]);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings`, {
          headers: getAuthHeader(),
        });
        setSettings(response.data);
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        toast.error("Failed to load settings");
      }
    };
    fetchSettings();
  }, [getAuthHeader]);

  // Calculate price whenever inputs change (only if materials cost is entered)
  const calculatePrice = useCallback(async () => {
    if (!settings) return;
    
    // Don't calculate or show price if materials cost is empty
    if (!materialsCost || parseFloat(materialsCost) <= 0) {
      setCustomerPrice(null);
      setFormattedPrice("");
      return;
    }

    setIsCalculating(true);
    try {
      const response = await axios.post(
        `${API_URL}/calculate-price`,
        {
          labor_hours: laborHours,
          materials_cost: parseFloat(materialsCost) || 0,
          permit_fee: settings.enable_permit_fee ? parseFloat(permitFee) || 0 : 0,
          subcontractor_cost: settings.enable_subcontractor_cost ? parseFloat(subcontractorCost) || 0 : 0,
          manager_discount: parseFloat(managerDiscount) || 0,
          trade_type: tradeType,
          profit_margin: profitMargin,
        },
        { headers: getAuthHeader() }
      );
      setCustomerPrice(response.data.customer_price);
      setFormattedPrice(response.data.formatted_price);
    } catch (error) {
      console.error("Calculation error:", error);
    } finally {
      setIsCalculating(false);
    }
  }, [laborHours, materialsCost, permitFee, subcontractorCost, managerDiscount, tradeType, profitMargin, settings, getAuthHeader]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      calculatePrice();
    }, 300);
    return () => clearTimeout(debounce);
  }, [calculatePrice]);

  // Generate AI summary options
  const generateSummary = async () => {
    if (!technicianNotes.trim()) {
      toast.error("Please enter job notes first");
      return;
    }

    if (!customerPrice) {
      toast.error("Please enter materials cost first to calculate base price");
      return;
    }

    setIsGeneratingSummary(true);
    try {
      const response = await axios.post(
        `${API_URL}/generate-summary`,
        { 
          technician_notes: technicianNotes,
          base_price: customerPrice,
          trade_type: tradeType
        },
        { headers: getAuthHeader() }
      );
      setServiceOptions(response.data.options);
      setSelectedOption(response.data.options[0]); // Select first option by default
      toast.success("Service options generated!");
    } catch (error) {
      console.error("Summary generation error:", error);
      // Fallback options when API fails
      const basePrice = customerPrice;
      const price2 = Math.round(basePrice * 1.15);
      const price3 = Math.round(price2 * 1.15);
      
      const optionNames = tradeType === "hvac" 
        ? ["Broken", "Broken and Worn", "Broken, Worn and Upgrade", "Complete Replacement - Consult Project Manager"]
        : ["Broken", "Broken and Worn", "Broken, Worn and Upgrade", "Premium Upgrade Option"];
      
      const consultText = tradeType === "hvac"
        ? "This option requires a complete system replacement. A Project Manager will assess your home and provide a custom quote."
        : "This premium upgrade option involves significant improvements. Please contact us for a custom assessment.";
      
      const mockOptions = [
        {
          tier: 1,
          name: optionNames[0],
          description: "We will repair the immediate issue to restore proper function.",
          price: basePrice,
          formatted_price: `$${basePrice.toLocaleString()}`,
          is_consultation: false,
          is_membership: false
        },
        {
          tier: 2,
          name: optionNames[1],
          description: "Includes the basic repair plus replacement of worn components likely to fail soon.",
          price: price2,
          formatted_price: `$${price2.toLocaleString()}`,
          is_consultation: false,
          is_membership: false
        },
        {
          tier: 3,
          name: optionNames[2],
          description: "Includes all repairs plus upgraded components for improved performance and longevity.",
          price: price3,
          formatted_price: `$${price3.toLocaleString()}`,
          is_consultation: false,
          is_membership: false
        },
        {
          tier: 4,
          name: optionNames[3],
          description: consultText,
          price: null,
          formatted_price: "Consult PM",
          is_consultation: true,
          is_membership: false
        },
        {
          tier: 5,
          name: "Blue Bear Membership",
          description: "Priority scheduling, annual inspections, 15% off repairs, no overtime charges.",
          price: 2999,
          formatted_price: "$29.99/month",
          is_consultation: false,
          is_membership: true
        }
      ];
      setServiceOptions(mockOptions);
      setSelectedOption(mockOptions[0]);
      toast.warning("Using demo options - AI temporarily unavailable");
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // Reset form for new entry
  const resetForm = () => {
    setLaborHours(1);
    setMaterialsCost("");
    setPermitFee("");
    setSubcontractorCost("");
    setManagerDiscount("");
    setProfitMargin(0.57);
    setTechnicianNotes("");
    setServiceOptions([]);
    setSelectedOption(null);
    setLastSavedOption(null);
    toast.success("Form cleared for new entry");
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#003366] rounded-lg flex items-center justify-center">
              <span className="text-white font-oswald font-bold text-sm">BB</span>
            </div>
            <div>
              <h1 className="font-oswald font-bold text-[#003366] text-lg tracking-tight leading-tight">BLUE BEAR</h1>
              <p className="text-xs text-slate-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/admin/settings")}
                  className="text-slate-600"
                  data-testid="admin-settings-btn"
                >
                  <Settings className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/sheet")}
                  className="text-slate-600"
                  data-testid="sheet-btn"
                >
                  <FileSpreadsheet className="w-5 h-5" />
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/history")}
              className="text-slate-600"
              data-testid="history-btn"
            >
              <History className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-600"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Trade Type Toggle */}
        <div className="max-w-md mx-auto mt-3">
          <div className="flex bg-slate-100 rounded-xl p-1">
            <button
              onClick={() => setTradeType("plumbing")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-bold text-sm transition-all ${
                tradeType === "plumbing"
                  ? "bg-[#003366] text-white shadow-md"
                  : "text-slate-600 hover:text-slate-800"
              }`}
              data-testid="trade-plumbing-btn"
            >
              <Droplets className="w-4 h-4" />
              PLUMBING
            </button>
            <button
              onClick={() => setTradeType("hvac")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-bold text-sm transition-all ${
                tradeType === "hvac"
                  ? "bg-[#FF6B00] text-white shadow-md"
                  : "text-slate-600 hover:text-slate-800"
              }`}
              data-testid="trade-hvac-btn"
            >
              <Flame className="w-4 h-4" />
              HVAC
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-md mx-auto px-4 py-6 space-y-6 pb-24">
        {/* Price Display */}
        <Card className="bg-[#003366] text-white border-0 shadow-xl overflow-hidden">
          <CardContent className="p-6 text-center">
            <p className="text-blue-200 text-xs uppercase tracking-wider mb-2">Customer Price</p>
            <div className="price-display text-5xl md:text-6xl" data-testid="customer-price-display">
              {isCalculating ? (
                <div className="w-8 h-8 mx-auto border-2 border-white border-t-transparent rounded-full spinner" />
              ) : formattedPrice ? (
                formattedPrice
              ) : (
                <span className="text-3xl text-blue-200">Enter materials cost</span>
              )}
            </div>
            <p className="text-blue-200 text-sm mt-2">
              {formattedPrice ? "Includes labor and materials" : "Price will appear after entering costs"}
            </p>
            {formattedPrice && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => copyToClipboard(formattedPrice, "Price")}
                className="mt-4 bg-white/10 hover:bg-white/20 text-white border-0"
                data-testid="copy-price-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Price
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Labor & Materials Section */}
        <Card className="card-hover">
          <CardContent className="p-6 space-y-5">
            {/* Labor Hours */}
            <div className="space-y-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Labor Time
              </Label>
              <p className="text-xs text-slate-500 -mt-1">
                (Hours reflect actual wrench time working on the job)
              </p>
              
              <Select value={laborHours.toString()} onValueChange={(val) => setLaborHours(parseFloat(val))}>
                <SelectTrigger className="h-14 text-lg rounded-xl border-2" data-testid="labor-hours-select">
                  <SelectValue placeholder="Select time">
                    {formatHoursDisplay(laborHours)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {laborHoursOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()} className="text-lg">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Working Window Display */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 text-center mb-2">
                  Working Window
                </p>
                <div className="flex items-center justify-center gap-2">
                  <span className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600">
                    {getWorkingWindow(laborHours).min}
                  </span>
                  <span className="text-slate-400">—</span>
                  <span className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600">
                    {getWorkingWindow(laborHours).max}
                  </span>
                </div>
              </div>
            </div>

            {/* Materials Cost */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Materials Cost
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={materialsCost}
                  onChange={(e) => setMaterialsCost(e.target.value)}
                  className="h-14 pl-12 text-lg rounded-xl border-2"
                  data-testid="materials-cost-input"
                />
              </div>
            </div>

            {/* Optional: Permit Fee */}
            {settings?.enable_permit_fee && (
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Permit / Disposal Fee
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={permitFee}
                    onChange={(e) => setPermitFee(e.target.value)}
                    className="h-14 pl-12 text-lg rounded-xl border-2"
                    data-testid="permit-fee-input"
                  />
                </div>
              </div>
            )}

            {/* Optional: Subcontractor Cost */}
            {settings?.enable_subcontractor_cost && (
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Subcontractor Cost
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={subcontractorCost}
                    onChange={(e) => setSubcontractorCost(e.target.value)}
                    className="h-14 pl-12 text-lg rounded-xl border-2"
                    data-testid="subcontractor-cost-input"
                  />
                </div>
              </div>
            )}

            {/* Manager Discount */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Manager Discount
                <span className="text-orange-500 font-medium normal-case">(needs approval)</span>
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={managerDiscount}
                  onChange={(e) => setManagerDiscount(e.target.value)}
                  className="h-14 pl-12 text-lg rounded-xl border-2"
                  data-testid="manager-discount-input"
                />
              </div>
            </div>

            {/* Profit Scale Slider */}
            <div className="space-y-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4" />
                Profit Scale
              </Label>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-slate-500">Lower</span>
                  <span className="text-sm text-slate-500">Higher</span>
                </div>
                <input
                  type="range"
                  min="52"
                  max="62"
                  value={Math.round(profitMargin * 100)}
                  onChange={(e) => setProfitMargin(parseInt(e.target.value) / 100)}
                  className="w-full h-2 bg-slate-300 rounded-lg appearance-none cursor-pointer slider-thumb-bland"
                  data-testid="profit-scale-slider"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Job Description Section */}
        <Card className="card-hover">
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Job Description
              </Label>
              <p className="text-xs text-slate-500">
                Type how you'd explain it to another technician. Shorthand or bullet points are OK.
              </p>
              <Textarea
                placeholder="e.g., Pulled toilet, replaced wax ring, reset, tested"
                value={technicianNotes}
                onChange={(e) => setTechnicianNotes(e.target.value)}
                className="min-h-[120px] text-base rounded-xl border-2 resize-none"
                data-testid="technician-notes-input"
              />
            </div>

            <Button
              onClick={generateSummary}
              disabled={isGeneratingSummary || !technicianNotes.trim()}
              className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-[#FF6B00] hover:bg-[#e56000] text-white rounded-xl shadow-lg active:scale-95 transition-transform"
              data-testid="generate-summary-btn"
            >
              {isGeneratingSummary ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full spinner" />
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Summary
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Service Options Section */}
        {serviceOptions.length > 0 && (
          <Card className="card-hover animate-fade-in border-[#003366]/20">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-oswald font-bold text-[#003366] text-lg uppercase tracking-tight">
                  Service Options
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={generateSummary}
                  disabled={isGeneratingSummary}
                  className="text-slate-500"
                  data-testid="regenerate-summary-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${isGeneratingSummary ? "spinner" : ""}`} />
                </Button>
              </div>
              
              <div className="space-y-3">
                {serviceOptions.map((option) => (
                  <div
                    key={option.tier}
                    onClick={() => setSelectedOption(option)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      option.is_membership
                        ? selectedOption?.tier === option.tier
                          ? "border-green-500 bg-green-50"
                          : "border-green-200 bg-green-50/50 hover:border-green-300"
                        : option.is_consultation
                        ? selectedOption?.tier === option.tier
                          ? "border-orange-500 bg-orange-50"
                          : "border-orange-200 bg-orange-50/50 hover:border-orange-300"
                        : selectedOption?.tier === option.tier
                        ? "border-[#003366] bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid={`option-${option.tier}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          option.is_membership
                            ? "bg-green-500 text-white"
                            : option.is_consultation
                            ? "bg-orange-500 text-white"
                            : selectedOption?.tier === option.tier
                            ? "bg-[#003366] text-white"
                            : "bg-slate-200 text-slate-600"
                        }`}>
                          {option.tier}
                        </div>
                        <span className={`font-bold ${
                          option.is_membership ? "text-green-700" : option.is_consultation ? "text-orange-700" : "text-[#003366]"
                        }`}>
                          {option.name}
                        </span>
                      </div>
                      <span className={`font-oswald font-bold text-lg ${
                        option.is_membership 
                          ? "text-green-600" 
                          : option.is_consultation 
                          ? "text-orange-600 text-base"
                          : "text-[#FF6B00]"
                      }`}>
                        {option.formatted_price}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 pl-8">{option.description}</p>
                  </div>
                ))}
              </div>

              {selectedOption && (
                <div className="space-y-3 pt-2">
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => copyToClipboard(
                        `${selectedOption.name}\n${selectedOption.formatted_price}\n\n${selectedOption.description}`,
                        "Option"
                      )}
                      className="flex-1 h-12 rounded-xl"
                      data-testid="copy-summary-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Selected
                    </Button>
                    <div className="flex-1 h-12 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center text-green-600 text-sm font-medium">
                      {isSaving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full spinner mr-2" />
                          Saving...
                        </>
                      ) : lastSavedOption ? (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Auto-saved
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Will auto-save
                        </>
                      )}
                    </div>
                  </div>
                  
                  {lastSavedOption && (
                    <Button
                      onClick={resetForm}
                      className="w-full h-12 bg-[#003366] hover:bg-[#002244] rounded-xl"
                      data-testid="new-entry-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Entry
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
